import {Auth} from '@aws-amplify/auth';
import {useNavigate} from 'react-router-dom';
import {Account} from '../types';
import {ActivityEventsService} from '../../debug-mode/activity-events/services/ActivityEventsService';
import {useAccounts} from './useAccounts';
import {checkAccountSession} from '../../account-sessions/services/AccountSessionService';

export const useAccountActions = () => {
    const navigate = useNavigate();
    const {handleUpdateAccount, handleDeleteAccount} = useAccounts();

    const redirectToApp = (account: Account) => {
        ActivityEventsService.add(
            'Authentication Verified',
            `User already authenticated for: ${account.emailAddress}`,
            account.emailAddress
        );
        navigate(`/accounts/${account.id}/redirect`);
    };

    const redirectToSignIn = (account: Account) => {
        ActivityEventsService.add(
            'Authentication Required',
            `User needs to authenticate for: ${account.emailAddress}`,
            account.emailAddress
        );
        navigate(`/accounts/${account.id}/method-selection`);
    };

    const handleContinue = async (account: Account) => {
        ActivityEventsService.add(
            'Account Selected',
            `Selected account: ${account.emailAddress}`,
            account.emailAddress
        );

        const sessionInfo = await checkAccountSession(account);

        if (sessionInfo.isActive && !sessionInfo.isExpired) {
            redirectToApp(account);
        } else {
            redirectToSignIn(account);
        }
    };

    const handleSignOutAndForget = async (account: Account) => {
        handleDeleteAccount(account.id);

        Auth.configure({
            userPoolId: account.cognitoUserPoolId,
            userPoolWebClientId: account.appClientId,
        });

        try {
            await Auth.signOut({global: true});
        } catch (e) {
            await Auth.signOut();
        }

        ActivityEventsService.add(
            'User Signed Out',
            `Signed out from: ${account.emailAddress}`,
            account.emailAddress
        );

        return false;
    };

    const handleSignOut = async (account: Account) => {
        Auth.configure({
            userPoolId: account.cognitoUserPoolId,
            userPoolWebClientId: account.appClientId,
        });

        try {
            await Auth.signOut({global: true});
        } catch (e) {
            await Auth.signOut();
        }

        ActivityEventsService.add(
            'User Signed Out',
            `Signed out from: ${account.emailAddress}`,
            account.emailAddress
        );

        const updated = Object.assign({}, account, {updatedAt: new Date().toISOString()});
        handleUpdateAccount(account.id, updated);

        return false;
    };

    const handleHideAccount = async (account: Account) => {
        const updated = Object.assign({}, account, {isVisible: 'no'});
        handleUpdateAccount(account.id, updated);
        return false;
    };

    return {
        handleContinue,
        handleSignOut,
        handleSignOutAndForget,
        handleHideAccount,
    };
};
